import { useState, useEffect } from "react";
import { useAuth } from "../../auth/AuthProvider";
import { Loader, X, CheckCircle2 } from "lucide-react";
import { useTranslation } from 'react-i18next';

function MarkEntry() {
  const { api, user } = useAuth();
  const { t, i18n } = useTranslation('markentry');
  const isRTL = i18n.language === 'ar';

  // State for all data
  const [academicYears, setAcademicYears] = useState([]);
  const [selectedYear, setSelectedYear] = useState(null);
  const [selectedTerm, setSelectedTerm] = useState(null);
  const [exams, setExams] = useState([]);
  const [selectedExam, setSelectedExam] = useState(null);
  const [subjects, setSubjects] = useState([]);
  const [selectedSubject, setSelectedSubject] = useState(null);
  const [classes, setClasses] = useState([]);
  const [selectedClass, setSelectedClass] = useState(null);
  const [selectedStream, setSelectedStream] = useState(null);
  const [students, setStudents] = useState([]);
  const [scores, setScores] = useState({});
  const [remarks, setRemarks] = useState({});

  // UI states
  const [loading, setLoading] = useState(false);
  const [submitting, setSubmitting] = useState(false);
  const [errors, setErrors] = useState([]);
  const [success, setSuccess] = useState(false);
  const [currentPage, setCurrentPage] = useState(1);
  const [totalPages, setTotalPages] = useState(1);

  // Fetch academic years on component mount
  useEffect(() => {
    fetchAcademicYears();
  }, []);

  // Fetch exams when year and term are selected
  useEffect(() => {
    if (selectedYear && selectedTerm) {
      fetchExams();
      fetchSubjects();
    }
  }, [selectedYear, selectedTerm]);

  // Fetch classes when exam is selected
  useEffect(() => {
    if (selectedExam) {
      fetchClasses();
    }
  }, [selectedExam]);

  // Fetch students when stream is selected
  useEffect(() => {
    if (selectedStream) {
      fetchStudents();
    }
  }, [selectedStream, currentPage]);

  // Reset states when selections change
  useEffect(() => {
    setSelectedTerm(null);
    setExams([]);
    setSelectedExam(null);
    setSelectedClass(null);
    setSelectedStream(null);
    setStudents([]);
    setScores({});
    setRemarks({});
  }, [selectedYear]);

  useEffect(() => {
    setSelectedExam(null);
    setSelectedClass(null);
    setSelectedStream(null);
    setStudents([]);
    setScores({});
    setRemarks({});
  }, [selectedTerm]);

  useEffect(() => {
    setSelectedClass(null);
    setSelectedStream(null);
    setStudents([]);
    setScores({});
    setRemarks({});
  }, [selectedExam]);

  useEffect(() => {
    setSelectedStream(null);
    setStudents([]);
    setScores({});
    setRemarks({});
  }, [selectedClass]);

  useEffect(() => {
    setStudents([]);
    setScores({});
    setRemarks({});
  }, [selectedStream]);

  // API functions
  const fetchAcademicYears = async () => {
    setLoading(true);
    try {
      const response = await api.get("/api/v1/academic-years/list-with-terms");
      setAcademicYears(response.data);

      // Auto-select current year if available
      const currentYear = response.data.find((year) => year.is_current_year);
      if (currentYear) {
        setSelectedYear(currentYear);
      }
    } catch (error) {
      setErrors([{ msg: t('errors.failedToFetchAcademicYears') }]);
    } finally {
      setLoading(false);
    }
  };

  const fetchExams = async () => {
    setLoading(true);
    try {
      const response = await api.get(
        `/api/v1/exams/school/${user.school_id}/all-exams`
      );
      // Filter exams by selected year and term
      const filteredExams = response.data.filter(
        (exam) =>
          exam.academic_year_id === selectedYear.academic_year_id &&
          exam.term_id === selectedTerm.term_id
      );
      setExams(filteredExams);
    } catch (error) {
      setErrors([{ msg: t('errors.failedToFetchExams') }]);
    } finally {
      setLoading(false);
    }
  };

  const fetchSubjects = async () => {
    setLoading(true);
    try {
      const response = await api.get("/api/v1/subjects");
      setSubjects(response.data);
    } catch (error) {
      setErrors([{ msg: t('errors.failedToFetchSubjects') }]);
    } finally {
      setLoading(false);
    }
  };

  const fetchClasses = async () => {
    setLoading(true);
    try {
      const response = await api.get(
        `/api/v1/admin/schools/${user.school_reg}/classes`
      );
      // Filter classes that are included in the selected exam
      const filteredClasses = response.data.filter((cls) =>
        selectedExam.class_ids.includes(cls.id)
      );
      setClasses(filteredClasses);
    } catch (error) {
      setErrors([{ msg: t('errors.failedToFetchClasses') }]);
    } finally {
      setLoading(false);
    }
  };

  const fetchStudents = async () => {
    setLoading(true);
    try {
      const response = await api.get(
        `/api/v1/admin/schools/${user.school_reg}/streams/${selectedStream.id}/students?page=${currentPage}&page_size=50`
      );

      if (currentPage === 1) {
        setStudents(response.data.items);
      } else {
        setStudents((prev) => [...prev, ...response.data.items]);
      }

      setTotalPages(response.data.total_pages);

      // If there are more pages, fetch them automatically
      if (currentPage < response.data.total_pages) {
        setCurrentPage((prev) => prev + 1);
      }

      // Initialize scores object for all students
      const newScores = { ...scores };
      response.data.items.forEach((student) => {
        if (!newScores[student.id]) {
          newScores[student.id] = "";
        }
      });
      setScores(newScores);
    } catch (error) {
      setErrors([{ msg: t('errors.failedToFetchStudents') }]);
    } finally {
      setLoading(false);
    }
  };

  const handleScoreChange = (studentId, value) => {
    // Only allow numbers between 0-100
    if (value === "" || (Number(value) >= 0 && Number(value) <= 100)) {
      setScores((prev) => ({
        ...prev,
        [studentId]: value,
      }));
    }
  };

  const handleRemarkChange = (studentId, value) => {
    setRemarks((prev) => ({
      ...prev,
      [studentId]: value,
    }));
  };

  const submitScores = async () => {
    setErrors([]);
    setSuccess(false);
    setSubmitting(true);

    try {
      // Validate that all students have scores
      const missingScores = students.filter(
        (student) =>
          scores[student.id] === "" || scores[student.id] === undefined
      );

      if (missingScores.length > 0) {
        setErrors([
          {
            msg: t('errors.enterScoresForAllStudents', { count: missingScores.length }),
          },
        ]);
        setSubmitting(false);
        return;
      }

      // Execute API calls one by one to better handle individual errors
      const failedSubmissions = [];
      const successfulSubmissions = [];

      for (const student of students) {
        try {
          const payload = [
            {
              subject_id: selectedSubject.id,
              raw_score: Number(scores[student.id]),
              remarks: remarks[student.id] || "",
              student_id: student.id,
            },
          ];

          await api.post(
            `/api/v1/exam-scores/school/${user.school_id}/student/${student.id}/exam/${selectedExam.id}/create`,
            payload
          );

          successfulSubmissions.push(student.id);
        } catch (studentError) {
          if (
            studentError.response &&
            studentError.response.data &&
            studentError.response.data.detail
          ) {
            const errorDetail = studentError.response.data.detail;

            if (typeof errorDetail === "string") {
              const scoreExistsMatch = errorDetail.match(
                /Score already exists for student (\d+) in subject (\d+)/
              );

              if (scoreExistsMatch) {
                const studentId = parseInt(scoreExistsMatch[1]);
                const subjectId = parseInt(scoreExistsMatch[2]);

                const studentObj = students.find((s) => s.id === studentId);
                const studentName = studentObj
                  ? studentObj.name
                  : `${t('labels.student')} ${studentId}`;

                const subjectName =
                  selectedSubject.id === subjectId
                    ? selectedSubject.subject_name
                    : subjects.find((s) => s.id === subjectId)?.subject_name ||
                      `${t('labels.subject')} ${subjectId}`;

                failedSubmissions.push({
                  msg: t('errors.scoreAlreadyExists', { studentName, subjectName }),
                });
              } else {
                failedSubmissions.push({ msg: errorDetail });
              }
            } else {
              failedSubmissions.push({
                msg: t('errors.failedToSubmitScoreFor', { studentName: student.name }),
              });
            }
          } else {
            failedSubmissions.push({
              msg: t('errors.failedToSubmitScoreFor', { studentName: student.name }),
            });
          }
        }
      }

      if (failedSubmissions.length > 0) {
        setErrors(failedSubmissions);

        // If some submissions were successful, show partial success message
        if (successfulSubmissions.length > 0) {
          setSuccess(true);
          setTimeout(() => setSuccess(false), 5000);

          // Reset scores and remarks for successfully submitted students
          const newScores = { ...scores };
          const newRemarks = { ...remarks };
          successfulSubmissions.forEach((studentId) => {
            newScores[studentId] = "";
            newRemarks[studentId] = "";
          });
          setScores(newScores);
          setRemarks(newRemarks);
        }
      } else {
        // All submissions were successful
        setSuccess(true);
        setTimeout(() => setSuccess(false), 5000);

        // Reset all scores and remarks
        const newScores = {};
        const newRemarks = {};
        students.forEach((student) => {
          newScores[student.id] = "";
          newRemarks[student.id] = "";
        });
        setScores(newScores);
        setRemarks(newRemarks);
      }
    } catch (error) {
      // General error handling code remains unchanged
      if (error.response && error.response.data && error.response.data.detail) {
        const errorDetail = error.response.data.detail;

        if (typeof errorDetail === "string") {
          const scoreExistsMatch = errorDetail.match(
            /Score already exists for student (\d+) in subject (\d+)/
          );

          if (scoreExistsMatch) {
            const studentId = parseInt(scoreExistsMatch[1]);
            const subjectId = parseInt(scoreExistsMatch[2]);

            const studentObj = students.find((s) => s.id === studentId);
            const studentName = studentObj
              ? studentObj.name
              : `${t('labels.student')} ${studentId}`;

            const subjectName =
              selectedSubject.id === subjectId
                ? selectedSubject.subject_name
                : subjects.find((s) => s.id === subjectId)?.subject_name ||
                  `${t('labels.subject')} ${subjectId}`;

            setErrors([
              {
                msg: t('errors.scoreAlreadyExists', { studentName, subjectName }),
              },
            ]);
          } else {
            setErrors([{ msg: errorDetail }]);
          }
        } else {
          setErrors([{ msg: t('errors.failedToSubmitScores') }]);
        }
      } else {
        setErrors([{ msg: t('errors.failedToSubmitScores') }]);
      }
    } finally {
      setSubmitting(false);
    }
  };

  const clearSelections = () => {
    setSelectedYear(null);
    setSelectedTerm(null);
    setSelectedExam(null);
    setSelectedSubject(null);
    setSelectedClass(null);
    setSelectedStream(null);
    setStudents([]);
    setScores({});
    setRemarks({});
    setCurrentPage(1);
  };

  return (
    <div className={`min-h-screen bg-gray-50 p-4 md:p-6 ${isRTL ? 'rtl' : 'ltr'}`} dir={isRTL ? 'rtl' : 'ltr'}>
      <div className="max-w-7xl mx-auto">
        <h1 className="text-2xl md:text-3xl font-bold text-gray-800 mb-6">
          {t('title')}
        </h1>

        {/* Error display */}
        {errors.length > 0 && (
          <div className="mb-6 bg-red-50 border border-red-200 rounded-lg p-4">
            <ul className={`list-disc ${isRTL ? 'pr-5' : 'pl-5'} text-red-700`}>
              {errors.map((error, index) => (
                <li key={index}>{error.msg}</li>
              ))}
            </ul>
          </div>
        )}

        {/* Success message */}
        {success && (
          <div className="mb-6 bg-green-50 border border-green-200 rounded-lg p-4 flex items-center">
            <CheckCircle2 size={20} className={`text-green-500 ${isRTL ? 'ml-2' : 'mr-2'}`} />
            <p className="text-green-700">{t('messages.scoresSubmittedSuccessfully')}</p>
          </div>
        )}

        <div className="bg-white rounded-xl shadow-sm border border-gray-100 p-5 mb-6">
          <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-4 mb-6">
            {/* Academic Year Selection */}
            <div>
              <label className="block text-sm font-medium text-gray-700 mb-1">
                {t('labels.academicYear')}
              </label>
              <div className="relative">
                <select
                  className={`bg-gray-50 block w-full ${isRTL ? 'pr-3 pl-10' : 'pl-3 pr-10'} py-2 text-base border border-gray-300 focus:outline-none focus:ring-2 focus:ring-teal-500 focus:border-teal-500 rounded-md`}
                  value={selectedYear ? selectedYear.academic_year_id : ""}
                  onChange={(e) => {
                    const yearId = Number(e.target.value);
                    const year = academicYears.find(
                      (y) => y.academic_year_id === yearId
                    );
                    setSelectedYear(year);
                  }}
                  disabled={loading}
                >
                  <option value="">{t('placeholders.selectAcademicYear')}</option>
                  {academicYears.map((year) => (
                    <option
                      key={year.academic_year_id}
                      value={year.academic_year_id}
                    >
                      {year.year_name} {year.is_current_year ? `(${t('labels.current')})` : ""}
                    </option>
                  ))}
                </select>
              </div>
            </div>

            {/* Term Selection */}
            <div>
              <label className="block text-sm font-medium text-gray-700 mb-1">
                {t('labels.term')}
              </label>
              <div className="relative">
                <select
                  className={`bg-gray-50 block w-full ${isRTL ? 'pr-3 pl-10' : 'pl-3 pr-10'} py-2 text-base border border-gray-300 focus:outline-none focus:ring-2 focus:ring-teal-500 focus:border-teal-500 rounded-md`}
                  value={selectedTerm ? selectedTerm.term_id : ""}
                  onChange={(e) => {
                    const termId = Number(e.target.value);
                    const term = selectedYear.terms.find(
                      (t) => t.term_id === termId
                    );
                    setSelectedTerm(term);
                  }}
                  disabled={!selectedYear || loading}
                >
                  <option value="">{t('placeholders.selectTerm')}</option>
                  {selectedYear?.terms.map((term) => (
                    <option key={term.term_id} value={term.term_id}>
                      {term.term_name}
                    </option>
                  ))}
                </select>
              </div>
            </div>

            {/* Exam Selection */}
            <div>
              <label className="block text-sm font-medium text-gray-700 mb-1">
                {t('labels.exam')}
              </label>
              <div className="relative">
                <select
                  className={`bg-gray-50 block w-full ${isRTL ? 'pr-3 pl-10' : 'pl-3 pr-10'} py-2 text-base border border-gray-300 focus:outline-none focus:ring-2 focus:ring-teal-500 focus:border-teal-500 rounded-md`}
                  value={selectedExam ? selectedExam.id : ""}
                  onChange={(e) => {
                    const examId = Number(e.target.value);
                    const exam = exams.find((ex) => ex.id === examId);
                    setSelectedExam(exam);
                  }}
                  disabled={!selectedTerm || loading || exams.length === 0}
                >
                  <option value="">{t('placeholders.selectExam')}</option>
                  {exams.map((exam) => (
                    <option key={exam.id} value={exam.id}>
                      {exam.exam_name}
                    </option>
                  ))}
                </select>
              </div>
            </div>

            {/* Subject Selection */}
            <div>
              <label className="block text-sm font-medium text-gray-700 mb-1">
                {t('labels.subject')}
              </label>
              <div className="relative">
                <select
                  className={`bg-gray-50 block w-full ${isRTL ? 'pr-3 pl-10' : 'pl-3 pr-10'} py-2 text-base border border-gray-300 focus:outline-none focus:ring-2 focus:ring-teal-500 focus:border-teal-500 rounded-md`}
                  value={selectedSubject ? selectedSubject.id : ""}
                  onChange={(e) => {
                    const subjectId = Number(e.target.value);
                    const subject = subjects.find((s) => s.id === subjectId);
                    setSelectedSubject(subject);
                  }}
                  disabled={!selectedTerm || loading || subjects.length === 0}
                >
                  <option value="">{t('placeholders.selectSubject')}</option>
                  {subjects.map((subject) => (
                    <option key={subject.id} value={subject.id}>
                      {subject.subject_name}
                    </option>
                  ))}
                </select>
              </div>
            </div>

            {/* Class Selection */}
            <div>
              <label className="block text-sm font-medium text-gray-700 mb-1">
                {t('labels.class')}
              </label>
              <div className="relative">
                <select
                  className={`bg-gray-50 block w-full ${isRTL ? 'pr-3 pl-10' : 'pl-3 pr-10'} py-2 text-base border border-gray-300 focus:outline-none focus:ring-2 focus:ring-teal-500 focus:border-teal-500 rounded-md`}
                  value={selectedClass ? selectedClass.id : ""}
                  onChange={(e) => {
                    const classId = Number(e.target.value);
                    const cls = classes.find((c) => c.id === classId);
                    setSelectedClass(cls);
                  }}
                  disabled={!selectedExam || loading || classes.length === 0}
                >
                  <option value="">{t('placeholders.selectClass')}</option>
                  {classes.map((cls) => (
                    <option key={cls.id} value={cls.id}>
                      {cls.name}
                    </option>
                  ))}
                </select>
              </div>
            </div>

            {/* Stream Selection */}
            <div>
              <label className="block text-sm font-medium text-gray-700 mb-1">
                {t('labels.stream')}
              </label>
              <div className="relative">
                <select
                  className={`bg-gray-50 block w-full ${isRTL ? 'pr-3 pl-10' : 'pl-3 pr-10'} py-2 text-base border border-gray-300 focus:outline-none focus:ring-2 focus:ring-teal-500 focus:border-teal-500 rounded-md`}
                  value={selectedStream ? selectedStream.id : ""}
                  onChange={(e) => {
                    const streamId = Number(e.target.value);
                    const stream = selectedClass.streams.find(
                      (s) => s.id === streamId
                    );
                    setSelectedStream(stream);
                    setCurrentPage(1); // Reset to first page when stream changes
                  }}
                  disabled={!selectedClass || loading}
                >
                  <option value="">{t('placeholders.selectStream')}</option>
                  {selectedClass?.streams.map((stream) => (
                    <option key={stream.id} value={stream.id}>
                      {stream.name}
                    </option>
                  ))}
                </select>
              </div>
            </div>
          </div>

          <div className={`flex ${isRTL ? 'justify-start' : 'justify-end'}`}>
            <button
              className="flex items-center text-sm text-gray-600 hover:text-gray-800"
              onClick={clearSelections}
              disabled={loading || submitting}
            >
              <X size={16} className={isRTL ? 'ml-1' : 'mr-1'} />
              {t('buttons.clearSelections')}
            </button>
          </div>
        </div>

        {/* Students Table */}
        {selectedStream && students.length > 0 && (
          <div className="bg-white rounded-xl shadow-sm border border-gray-100 overflow-hidden">
            <div className="p-5 border-b border-gray-100">
              <h2 className="text-lg font-semibold text-gray-800">
                {t('table.studentsInStream', { 
                  streamName: selectedStream.name, 
                  subjectName: selectedSubject?.subject_name || t('labels.subject')
                })}
              </h2>
              <p className="text-sm text-gray-500 mt-1">
                {t('table.enterMarksFor', { 
                  examName: selectedExam?.exam_name || t('labels.exam'), 
                  studentCount: students.length 
                })}
              </p>
            </div>

            <div className="overflow-x-auto">
              <table className="min-w-full divide-y divide-gray-200">
                <thead className="bg-gray-50">
                  <tr>
                    <th
                      scope="col"
                      className={`px-6 py-3 ${isRTL ? 'text-right' : 'text-left'} text-xs font-medium text-gray-500 uppercase tracking-wider`}
                    >
                      {t('table.admissionNo')}
                    </th>
                    <th
                      scope="col"
                      className={`px-6 py-3 ${isRTL ? 'text-right' : 'text-left'} text-xs font-medium text-gray-500 uppercase tracking-wider`}
                    >
                      {t('table.studentName')}
                    </th>
                    <th
                      scope="col"
                      className={`px-6 py-3 ${isRTL ? 'text-right' : 'text-left'} text-xs font-medium text-gray-500 uppercase tracking-wider`}
                    >
                      {t('table.studentGender')}
                    </th>
                    <th
                      scope="col"
                      className={`px-6 py-3 ${isRTL ? 'text-right' : 'text-left'} text-xs font-medium text-gray-500 uppercase tracking-wider`}
                    >
                      {t('table.score')}
                    </th>
                    <th
                      scope="col"
                      className={`px-6 py-3 ${isRTL ? 'text-right' : 'text-left'} text-xs font-medium text-gray-500 uppercase tracking-wider`}
                    >
                      {t('table.remarks')}
                    </th>
                  </tr>
                </thead>
                <tbody className="bg-white divide-y divide-gray-200">
                  {[...students]
                    .sort((a, b) => a.admission_number - b.admission_number)
                    .map((student) => (
                      <tr key={student.id}>
                        <td className="px-6 py-4 whitespace-nowrap text-sm text-gray-900">
                          {student.admission_number}
                        </td>
                        <td className="px-6 py-4 whitespace-nowrap">
                          {student.name}
                        </td>
                        <td className="px-6 py-4 whitespace-nowrap">
                          <span
                            className={`px-2 py-1 rounded-xl text-xs font-medium ${
                              student.gender.toLowerCase() === "male"
                                ? "bg-blue-100 text-blue-800"
                                : "bg-pink-100 text-pink-800"
                            }`}
                          >
                            {t(`gender.${student.gender.toLowerCase()}`)}
                          </span>
                        </td>

                        <td className="px-6 py-4 whitespace-nowrap">
                          <input
                            type="number"
                            min="0"
                            max="100"
                            className="w-20 px-3 py-2 border border-gray-300 rounded-md focus:outline-none focus:ring-2 focus:ring-teal-500 focus:border-teal-500"
                            value={scores[student.id] || ""}
                            onChange={(e) =>
                              handleScoreChange(student.id, e.target.value)
                            }
                            disabled={submitting}
                          />
                        </td>
                        <td className="px-6 py-4 whitespace-nowrap">
                          <input
                            type="text"
                            className="w-full px-3 py-2 border border-gray-300 rounded-md focus:outline-none focus:ring-2 focus:ring-teal-500 focus:border-teal-500"
                            placeholder={t('placeholders.addRemarks')}
                            value={remarks[student.id] || ""}
                            onChange={(e) =>
                              handleRemarkChange(student.id, e.target.value)
                            }
                            disabled={submitting}
                          />
                        </td>
                      </tr>
                    ))}
                </tbody>
              </table>
            </div>

            <div className={`p-5 border-t border-gray-200 flex ${isRTL ? 'justify-start' : 'justify-end'}`}>
              <button
                className="px-6 py-2 bg-[#0E5252] text-white rounded-lg hover:bg-[#0a3e3e] focus:outline-none focus:ring-2 focus:ring-[#0E5252] focus:ring-offset-2 disabled:opacity-50 transition-colors flex items-center"
                onClick={submitScores}
                disabled={loading || submitting}
              >
                {submitting ? (
                  <>
                    <Loader size={16} className={`animate-spin ${isRTL ? 'ml-2' : 'mr-2'}`} />
                    {t('buttons.submitting')}
                  </>
                ) : (
                  t('buttons.submitScores')
                )}
              </button>
            </div>
          </div>
        )}

        {/* Loading State */}
        {loading && (
          <div className="flex justify-center items-center py-12">
            <Loader size={32} className="animate-spin text-[#0E5252]" />
            <span className={`${isRTL ? 'mr-3' : 'ml-3'} text-gray-600`}>{t('messages.loading')}</span>
          </div>
        )}

        {/* Empty State for Students */}
        {selectedStream && students.length === 0 && !loading && (
          <div className="bg-white rounded-xl shadow-sm border border-gray-100 p-10 text-center">
            <div className="text-gray-400 mb-3">
              <svg
                className="w-12 h-12 mx-auto"
                fill="none"
                stroke="currentColor"
                viewBox="0 0 24 24"
              >
                <path
                  strokeLinecap="round"
                  strokeLinejoin="round"
                  strokeWidth={1.5}
                  d="M12 4.354a4 4 0 110 5.292M15 21H3v-1a6 6 0 0112 0v1zm0 0h6v-1a6 6 0 00-9-5.197M13 7a4 4 0 11-8 0 4 4 0 018 0z"
                />
              </svg>
            </div>
            <h3 className="text-lg font-medium text-gray-900 mb-1">
              {t('messages.noStudentsFound')}
            </h3>
            <p className="text-gray-500">
              {t('messages.noStudentsEnrolled')}
            </p>
          </div>
        )}
      </div>
    </div>
  );
}

export default MarkEntry;